import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { Session, ClassContent, PracticeResultMap, VocabResult, PracticeResult, VocabRecordAttempts, VocabRecordType } from "../types/session";
import { UserRole } from "../types/user";
import { sessionService } from '../services';
import { useUserStore } from './user';
import { useClassStore } from './class';
import { SignalRRoom } from "../types/signalr";
import { PracticeQuizState } from "../types/class";
import { toast } from "vue3-toastify";
import { useLangStore } from "./lang";
import { TEXT } from "../configs/text";
import localforage from "localforage";
import { utils } from "../utils";


export const useSessionStore = defineStore('session', () => {
    const user = useUserStore()
    const classStore = useClassStore()
    const lang = useLangStore()

    const currentSession = ref<Session>(Object.values(Session)[0])
    const teacherSession = ref<Session | null>(null)
    const classContent = ref<ClassContent | null>(null)

    const rewardPointSessions = computed<Session[]>(() => {
        return Object.values(Session).filter(session => classContent.value?.[session].is_active === 1);
    })

    // splitting practice url out of class content due to user_id change when updating quiz/lecture data
    const practiceUrl = ref<{
        [Session.practice]: string | null
        [Session.practice2]: string | null
    }>({
        [Session.practice]: null,
        [Session.practice2]: null
    })
    const isFullscreen = ref<boolean>(user.userRole === UserRole.student && teacherSession ? true : false)

    // vocab state
    const allowSpeakingStudentIds = ref<number[]>([])
    const currentVocabId = ref<number>(0)
    const teacherVocabId = ref<number>(0)
    const vocabRecordingType = ref<VocabRecordType>(VocabRecordType.word)
    const vocabRecordAttempts = ref<VocabRecordAttempts>({})
    const recordAttemptCount = computed<number>((): number => {
        return vocabRecordAttempts.value[classStore.roomName]?.[user.username || '']?.[currentVocabId.value]?.[vocabRecordingType.value]?.length || 0
    })

    // practice state
    const practiceQuizState = ref<PracticeQuizState | null>(null)
    const practiceResult = ref<PracticeResultMap>({
        [Session.practice]: {},
        [Session.practice2]: {}
    })
    const currentPracticeResult = computed<PracticeResult | null>(() => {
        if (!classStore.roomName || !user.username) return null;
        return practiceResult.value[currentSession.value as Session.practice | Session.practice2]?.[classStore.roomName]?.[user.username] || null
    })

    async function getClassContent() {
        let classContentData: ClassContent | null = null;
        if (user.userRole === UserRole.student) {
            classContentData = await sessionService.getStudentClassContent()
        } else {
            const classId = classStore.selectedClassDetails?.id
            if (classId) {
                classContentData = await sessionService.getTeacherClassContent(classId)
            }
        }

        const practiceUrlData = classContentData?.[Session.practice].url || null
        const practice2UrlData = classContentData?.[Session.practice2].url || null
        // no practice url = not setup data in CMS
        // if (!practiceUrlData) {
        //     classContent.value = null
        //     practiceUrl.value = null
        //     return
        // }

        syncClassContent(classContentData)
        utils.preloadFromApiData(classContent.value)

        practiceUrl.value = {
            [Session.practice]: practiceUrlData,
            [Session.practice2]: practice2UrlData
        }

        // reset default current session for inactive session
        if (classContentData && classContentData[currentSession.value].is_active === 0) {
            for (const session in Session) {
                if (!classContentData[session as Session]?.is_active) continue;
                currentSession.value = session as Session
                break;
            }
        }
    }

    async function submitAudioVocab(sentence: string, audioFile: Blob, type: VocabRecordType, vocabId: number): Promise<VocabResult | null> {
        if (!classStore.roomName) return null;
        if (!user.username) return null;
        if (!currentVocabId.value) return null;
        const audioLength = await utils.getBlobDurationOffline(audioFile)
        const vocabResult = await sessionService.submitAudioVocab(sentence, audioFile, audioLength, type, vocabId)
        if (!vocabResult) return null;

        // save audio blob to index storage for later playback
        const audioKey = crypto.randomUUID()
        await localforage.setItem(audioKey, audioFile)
        vocabResult.key = audioKey

        vocabRecordAttempts.value[classStore.roomName] ??= {}
        vocabRecordAttempts.value[classStore.roomName][user.username] ??= {}
        vocabRecordAttempts.value[classStore.roomName][user.username][currentVocabId.value] ??= {}
        vocabRecordAttempts.value[classStore.roomName][user.username][currentVocabId.value][vocabRecordingType.value] ??= []
        vocabRecordAttempts.value[classStore.roomName][user.username][currentVocabId.value][vocabRecordingType.value].push(vocabResult)
        return vocabResult
    }

    async function setPracticeResult(result: PracticeResult) {
        if (!classStore.roomName || !user.username) return;

        if (!practiceResult.value[currentSession.value as Session.practice | Session.practice2][classStore.roomName]) {
            practiceResult.value[currentSession.value as Session.practice | Session.practice2][classStore.roomName] = {}
        }

        practiceResult.value[currentSession.value as Session.practice | Session.practice2][classStore.roomName][user.username] = result
    }

    function syncSession(room: SignalRRoom) {
        if (user.userRole === UserRole.student) {
            if (parseInt(room.currentQuestion || '0') && room.listStudentAnswer.some(roomUser => parseInt(roomUser.id) === user.userInfo?.id)) {
                currentVocabId.value = parseInt(room.currentQuestion || '0')
            }
        }

        if (room.currentScreen) {
            currentSession.value = room.currentScreen
        }

        teacherSession.value = room.currentScreen
        teacherVocabId.value = parseInt(room.currentQuestion || '0')
        practiceQuizState.value = room.practiceStart
        allowSpeakingStudentIds.value = room.listStudentAnswer.map(user => {
            return parseInt(user.id)
        })
    }

    function syncClassContent(content: ClassContent | undefined | null) {
        // map CDN url
        const contentCDN = utils.mapCDNUrls(content)
        classContent.value = contentCDN || null
    }

    watch(allowSpeakingStudentIds, (newList, oldList) => {
        if (user.userRole !== UserRole.student) return;
        if (oldList.includes(user.userInfo?.id || 0)) return;
        if (!newList.includes(user.userInfo?.id || 0)) return;
        if (currentSession.value !== Session.vocab) return;

        toast.info(lang.i18n.global.t(TEXT.you_can_now_record_your_speaking))
    })

    watch(teacherSession, () => {
        if (user.userRole !== UserRole.student) return;
        if (!teacherSession.value) {
            isFullscreen.value = false
            return
        }
        isFullscreen.value = true
    })

    return {
        currentSession,
        classContent,
        rewardPointSessions,
        practiceUrl,
        getClassContent,
        teacherSession,
        isFullscreen,
        allowSpeakingStudentIds,
        currentVocabId,
        teacherVocabId,
        vocabRecordAttempts,
        recordAttemptCount,
        submitAudioVocab,
        practiceQuizState,
        practiceResult,
        currentPracticeResult,
        setPracticeResult,
        syncSession,
        syncClassContent,
        vocabRecordingType
    }
}, {
    persist: {
        pick: ['currentSession', 'teacherSession', 'practiceResult', 'currentVocabId', 'vocabRecordAttempts']
    }
})